/**
 * useTourInit hook
 *
 * Encapsulates the full tour startup pattern that was duplicated across pages:
 * 1. Check ?tour=X query param → beginTour + resumeAt
 * 2. Check queued tour for this type → trigger → beginTour
 * 3. Ensure a tour exists (new user fallback) → beginTour
 *
 * Each page calls this hook once on mount. Returns the same shape as useTour
 * so pages can destructure what they need for rendering the overlay.
 *
 * IMPORTANT: effects use stable refs for options to avoid re-firing on every
 * render when options is created inline. Only searchParams changes trigger
 * re-evaluation.
 */

import { useCallback, useEffect, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import { useAuthStore } from "@/features/auth/store/authStore"
import {
  getAllTourStatus,
  getTourStatus,
  startTour as startTourApi,
  type TourStatus,
} from "../services/tour.service"
import { useTour } from "./useTour"

interface UseTourInitOptions {
  /** Metadata to store on the tour (e.g., ritualId for ritual tours) */
  metadata?: Record<string, unknown>
  /**
   * Called when a tour is auto-ensured (step 3 — no queued tour existed, so a fresh
   * one was created). If provided, the hook delegates to this callback instead of
   * calling beginTour() inline. Use this to redirect to a demo ritual page.
   */
  onEnsure?: (ensuredTour: {
    tourType: string
    metadata: Record<string, unknown> | null
  }) => Promise<void> | void
  /**
   * When true, skip the new-user "ensure" (step 3) entirely: a brand-new user
   * with no tour record is left untouched instead of auto-creating + starting the
   * tour. Used for deep-link landings so the tour doesn't redirect the user off
   * the page they were sent to. An already-queued or in-progress tour is
   * unaffected.
   */
  suppressEnsure?: boolean
}

export function useTourInit(
  tourType: "home_tour" | "ritual_tour",
  options: UseTourInitOptions = {},
) {
  const tour = useTour(tourType)
  const [searchParams, setSearchParams] = useSearchParams()
  const startedRef = useRef(false)
  const queuedCheckDoneRef = useRef(false)
  const createdRitualIdRef = useRef<string | null>(null)

  // Stable refs for options — prevents effect re-fires when options is an inline object
  const beginTourRef = useRef(tour.beginTour)
  beginTourRef.current = tour.beginTour
  const resumeAtRef = useRef(tour.resumeAt)
  resumeAtRef.current = tour.resumeAt
  const optionsRef = useRef(options)
  optionsRef.current = options

  // Stable beginTour wrapper that reads from ref.
  // Sets __tourActive so AppLayout skips the resume modal when this
  // page is already showing the tour overlay.
  const stableBeginTour = useCallback(
    (metadata?: Record<string, unknown>) => {
      ;(window as unknown as Record<string, unknown>).__tourActive = tourType
      return beginTourRef.current(metadata)
    },
    [tourType],
  )

  // StrictMode-safe unmount tracking: only clear __tourActive on real
  // unmount (navigation away), not on StrictMode's simulated remount.
  const unmountingRef = useRef(false)
  useEffect(() => {
    unmountingRef.current = false
    return () => {
      unmountingRef.current = true
      setTimeout(() => {
        if (unmountingRef.current) {
          delete (window as unknown as Record<string, unknown>).__tourActive
        }
      }, 0)
    }
  }, [])

  // STEP 1: Start tour from query param (e.g., ?tour=home&step=3 from resume modal)
  useEffect(() => {
    if (!useAuthStore.getState().isAuthenticated()) return
    if (startedRef.current) return
    const tourParam = searchParams.get("tour")
    const stepParam = searchParams.get("step")

    if (tourParam === tourType.replace("_tour", "")) {
      startedRef.current = true
      const createdId = searchParams.get("createdRitualId")
      if (createdId) createdRitualIdRef.current = createdId
      const step = stepParam ? parseInt(stepParam, 10) || 1 : 1
      const meta = optionsRef.current.metadata
      beginTourRef.current(meta).then(() => {
        if (step > 1) resumeAtRef.current(step)
      })
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams, tourType])

  // STEP 2 & 3: Auto-trigger from queue, or ensure for new users.
  // Uses a single getTourStatus call — no redundant API hits.
  // Only depends on searchParams; reads options from ref to avoid re-fires.
  useEffect(() => {
    if (!useAuthStore.getState().isAuthenticated()) return
    if (startedRef.current) return
    if (queuedCheckDoneRef.current) return
    const tourParam = searchParams.get("tour")
    if (tourParam) return
    // Set guard synchronously so StrictMode double-invoke is blocked immediately.
    queuedCheckDoneRef.current = true
    // Proactively set __tourActive to prevent AppLayout from making a duplicate
    // GET /api/user/tours call. If we discover an existing active tour that we
    // won't handle (returning user), we clear this flag and signal AppLayout.
    const win = window as unknown as Record<string, unknown>
    win.__tourActive = tourType
    ;(async () => {
      try {
        const opts = optionsRef.current
        let status: TourStatus

        // Home tours: check both types in one call to defer if ritual tour is active
        if (tourType === "home_tour") {
          const all = await getAllTourStatus()
          if (all.ritual_tour.active) {
            delete win.__tourActive
            return
          }
          status = all.home_tour
        } else {
          status = await getTourStatus(tourType)
        }

        // Check if a queued tour exists and trigger it
        if (status.queued) {
          const merged = {
            ...((status.queued.metadata as Record<string, unknown>) ?? {}),
            ...opts.metadata,
          }
          await stableBeginTour(merged)
          startedRef.current = true
          return
        }

        // New user — ensure a tour exists (if no record at all)
        if (!status.exists) {
          // Deep-link landing: do not auto-create/start the tour for this visit.
          // The user stays where they intended; the home tour still ensures on
          // their next Home visit. (issue #565)
          if (opts.suppressEnsure) {
            startedRef.current = true
            delete win.__tourActive
            return
          }
          if (opts.onEnsure) {
            const ensured = await startTourApi(tourType, opts.metadata)
            await opts.onEnsure(ensured)
          } else {
            await stableBeginTour(opts.metadata)
          }
          startedRef.current = true
          return
        }

        // Existing active tour — not a new user, not queued, just an in-progress
        // tour. Clear our flag and signal AppLayout to show the resume modal.
        delete win.__tourActive
        window.dispatchEvent(new CustomEvent("rituality:resumeCheck"))
      } catch {
        // Reset guards on error so a remount can retry
        queuedCheckDoneRef.current = false
        delete win.__tourActive
      }
    })()
  }, [searchParams, tourType, stableBeginTour])

  return {
    showOverlay: tour.showOverlay,
    currentStep: tour.currentStep,
    createdRitualId: createdRitualIdRef.current,
    beginTour: stableBeginTour,
    goNext: tour.goNext,
    goBack: tour.goBack,
    finishTour: tour.finishTour,
    dismissTour: tour.dismissTour,
    closeModal: tour.closeModal,
    resumeAt: tour.resumeAt,
  }
}
